
import {
  pushDataLayerEvent,
} from '~/utils/eventTracking'
import {
  getDefaultSubmitButton,
  getForm,
  getSubmitButton,
  getSubmitButtonAction,
  getSubmitButtonText,
  populateHiddenTrackingFields,
  extractFormMetadata,
} from '../utils/formie.js'


/**
 * @param {string} url
 * @returns {string}
 */
function removeTrailingSlash(url) {
  return url.replace(/\/$/, '')
}

/**
 * Constants for Formie form tracking event names
 */
export const FORMIE_EVENT = {
  BEFORE_SUBMIT: 'onBeforeFormieSubmit',
  AFTER_SUBMIT: 'onAfterFormieSubmit',
  SUBMIT_ERROR: 'onFormieSubmitError',
}
/**
 * Constants for GTM form tracking event names
 */
export const GTM_FORM_EVENT = {
  SUBMIT_ATTEMPT: 'ev_form_submit_attempt',
  SUBMIT_SUCCESS: 'ev_form_submit_success',
  SUBMIT_ERROR: 'ev_form_submit_error',
}

/**
 * Tracks Formie form submissions and pushes structured events to the GTM dataLayer.
 *
 * Listens for Formie events via the EventBus and handles three submission outcomes:
 * - External redirect: pushes the success event immediately, then delays the redirect
 *   to give GTM time to process it before the page unloads.
 * - Internal redirect: defers the success event to sessionStorage so it can be replayed
 *   on the next page load.
 * - Same-page submission: pushes the success event immediately.
 *
 * Also handles deferred events from a previous page load on construction.
 *
 * @see src/templates/_components/formie/trackAjaxFormSubmission.twig — routes Formie
 *   DOM events into the EventBus via window.APP.queue
 */
export default class FormieTracking {
  /** @type {string} */
  #sessionStorageKey = 'lastFormieSubmittedEvent'

  /** @type {WeakMap<Element, Element>} Maps each fui-form element to the submit button that was clicked */
  #clickedSubmitButton = new WeakMap()

  /**
   * @param {Object} trackingStore - Store exposing getCid(), getUtmParams(), getContextForDataLayer()
   * @param {Object} config - App config (siteHandle, environment, debugTracking)
   * @param {import('./EventBus').default} eventBus
   */
  constructor(trackingStore, config, eventBus) {
    this.trackingStore = trackingStore
    this.config = config
    this.eventBus = eventBus
    this.processDeferredEvent()
    this.bindDomEvents()
    this.bindBusEvents()
  }

  /**
   * Push a named GTM event for a given form to the dataLayer.
   * @param {string} event - GTM event name (see GTM_FORM_EVENT)
   * @param {Element} form - The fui-form element
   * @param {Object} [meta={}] - Additional payload properties
   */
  pushFormEvent(event, form, meta = {}) {
    pushDataLayerEvent(this.toGtmEvent(event, form, meta), this.config)
  }

  debugLog(message, data) {
    if (this.config.debugTracking) {
      console.info(`[formie-tracking] ${message}`, data)
    }
  }

  /**
   * Build a GTM dataLayer payload for a form event.
   * Merges form metadata, submit button metadata, extra meta, and tracking context.
   * @param {string} event - GTM event name
   * @param {Element} form - The fui-form element
   * @param {Object} [meta={}] - Additional payload properties
   * @returns {Object}
   */
  toGtmEvent(event, form, meta = {}) {
    return {
      event,
      ...extractFormMetadata(form),
      ...this.#extractSubmitButtonMeta(form),
      ...meta,
      ...this.trackingStore.getContextForDataLayer(),
    }
  }

  /**
   * Bind a capture-phase click listener to record which submit button was clicked
   * before the form submit event fires.
   */
  bindDomEvents() {
    document.addEventListener('click', (e) => this.handleSubmitClick(e), true)
  }

  /**
   * Subscribe to Formie events via the EventBus.
   *
   * onBeforeFormieSubmit: populates hidden cid/utm tracking fields and pushes an attempt event.
   * onAfterFormieSubmit: pushes a success event, with redirect handling (see below).
   */
  bindBusEvents() {
    this.eventBus.on(FORMIE_EVENT.BEFORE_SUBMIT, (e) => {
      const form = getForm(e.target)
      this.debugLog('Before submit received', {
        form: extractFormMetadata(form),
      })

      populateHiddenTrackingFields(form, {
        cid: this.trackingStore.getCid(),
        utmParams: this.trackingStore.getUtmParams(),
      })
      this.pushFormEvent(GTM_FORM_EVENT.SUBMIT_ATTEMPT, form)
    })

    this.eventBus.on(FORMIE_EVENT.AFTER_SUBMIT, (e) => {
      const form = getForm(e.target)
      const meta = {
        success_source: 'client_event',
      }
      this.debugLog('After submit received', {
        form: extractFormMetadata(form),
        redirectUrl: e.detail.redirectUrl,
        redirectsToExternalSite: this.formRedirectsToExternalSite(e.detail.redirectUrl),
        redirectsToInternalPage: this.formRedirectsToInternalPage(e.detail.redirectUrl),
      })
      if (this.formRedirectsToExternalSite(e.detail.redirectUrl)) {
        // Push the event immediately, then delay the redirect so GTM has time to process
        // the dataLayer push before the page unloads.
        this.debugLog('Pushing success before external redirect')
        this.pushFormEvent(GTM_FORM_EVENT.SUBMIT_SUCCESS, form, meta)
        this.deferRedirectUntilAfterTracking(e)
        return
      }
      if (this.formRedirectsToInternalPage(e.detail.redirectUrl)) {
        // Store the event in sessionStorage so it can be replayed on the next page load.
        this.debugLog('Deferring success event to next page load')
        return this.deferEventToNextPageLoad(GTM_FORM_EVENT.SUBMIT_SUCCESS, form, meta)
      }
      this.debugLog('Pushing success on current page')
      this.pushFormEvent(GTM_FORM_EVENT.SUBMIT_SUCCESS, form, meta)
    })

    // Note: onFormieSubmitError does not fire for FE validation errors on ajax forms,
    // so this handler is omitted.
    // this.eventBus.on(FORMIE_EVENT.SUBMIT_ERROR, (e) => { ... })
  }

  /**
   * Record the submit button that triggered the form submission so its text and
   * action can be included in the tracking payload.
   * @param {MouseEvent} event
   */
  handleSubmitClick(event) {
    const button = getSubmitButton(event.target)

    if (!button) {
      return
    }

    const form = button.closest('.fui-form')

    if (!form) {
      return
    }

    this.#clickedSubmitButton.set(form, button)
  }

  /**
   * Serialise the GTM event payload to sessionStorage so it can be pushed to the
   * dataLayer after the browser navigates to the next page.
   * @param {string} eventName - GTM event name
   * @param {Element} form - The fui-form element
   * @param {Object} [meta={}] - Additional payload properties
   */
  deferEventToNextPageLoad(eventName, form, meta = {}) {
    const payload = this.toGtmEvent(eventName, form, meta)
    this.debugLog('Setting deferred event in sessionStorage', payload)
    window.sessionStorage.setItem(this.#sessionStorageKey, JSON.stringify(payload))
  }

  /**
   * Extract submit button text and action from the button that was clicked,
   * falling back to the form's default submit button if no click was recorded.
   * @param {Element} form - The fui-form element
   * @returns {{ submit_button_text: string, submit_button_action: string } | {}}
   */
  #extractSubmitButtonMeta(form) {
    const button = this.#clickedSubmitButton.get(form) || getDefaultSubmitButton(form)
    if (!button) {
      return {}
    }
    return {
      submit_button_text: getSubmitButtonText(button),
      submit_button_action: getSubmitButtonAction(button),
    }
  }

  /**
   * On construction, check sessionStorage for an event deferred from a previous page
   * load and push it to the dataLayer if present.
   */
  processDeferredEvent() {
    const eventData = window.sessionStorage.getItem(this.#sessionStorageKey)

    if (eventData) {
      const event = JSON.parse(eventData)
      this.debugLog('Replaying deferred event from sessionStorage', event)
      pushDataLayerEvent(event, this.config)
      window.sessionStorage.removeItem(this.#sessionStorageKey)
      this.debugLog('Deferred event replayed and cleared')
    }
  }

  /**
   * Returns true if the redirect URL points to a different page on the same origin.
   * @param {string|null} redirectUrl
   * @returns {boolean}
   */
  formRedirectsToInternalPage(redirectUrl) {
    return redirectUrl
      && !this.formRedirectsToExternalSite(redirectUrl)
      && (removeTrailingSlash(redirectUrl) !== removeTrailingSlash(window.location.href))
  }

  /**
   * Returns true if the redirect URL points to a different origin.
   * @param {string|null} redirectUrl
   * @returns {boolean}
   */
  formRedirectsToExternalSite(redirectUrl) {
    if (!redirectUrl) {
      return false
    }
    try {
      const redirectOrigin = new URL(redirectUrl).origin
      return redirectOrigin !== window.location.origin
    } catch {
      return false
    }
  }

  /**
   * Prevent Formie's own redirect by nulling redirectUrl on the event detail,
   * then perform the redirect manually after a short delay to allow the GTM
   * dataLayer push to be processed before the page unloads.
   * @param {CustomEvent} e - The onAfterFormieSubmit event
   */
  deferRedirectUntilAfterTracking(e) {
    const formRedirectUrl = e.detail.redirectUrl
    const delay = 200 // ms to wait to allow the event to reach GTM
    e.detail.redirectUrl = null

    setTimeout(() => {
      if (formRedirectUrl) {
        window.location.href = formRedirectUrl
      }
    }, delay)
  }
}
